import React, { useState } from 'react';
import styled from 'styled-components';
import CustomCheckbox from '../Checkbox/CustomCheckbox';
import { Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import CustomCounter from '../CustomCounter/CustomCounter';
import CustomTooltip from '../CustomTootip/CustomTooltip';
import CustomButton from '../CustomButton/CustomButton';
import { IsMobileWidth } from '../MediaQueryUtils';
import { CrossIcon, EditIcon, TickIcon } from '../Icons';
import { useAppSelector } from '@src/store/hooks';
import { RESTAURANT } from '@src/Constants';

const MobileContainer = styled.div((props) => ({
	display: 'flex',
	justifyContent: 'space-between',
	background: parseInt(props?.quantity) > 0 ? '#FFFDF5' : '#FFFFFF',
	height: 'auto',
	width: '100%',
	padding: '16px',
	borderTop: '1px solid #E9E9E9',
	flexDirection: 'column',
}));

const StyledContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	background: '#FFFFFF',
	height: 'auto',
	width: '100%',
	padding: '16px',
	borderTop: '1px solid #E9E9E9',
	maxHeight: '200px',
	'#image-containner': {
		display: 'flex',
		flexDirection: 'column',
		gap: '14px',
	},
	'#image-containner img': {
		width: '140px',
		height: '140px',
		border: '1px solid #E9E9E9',
		borderRadius: '8px',
		objectFit: 'contain',
	},
	'#fieldsContainer': {
		display: 'flex',
		flexDirection: 'column',
		gap: '14px',
	},
	'#fields': {
		display: 'flex',
		gap: '14px',
	},
	'#quantity-recommend-container': {
		display: 'flex',
		flexDirection: 'column',
		gap: '14px',
		marginLeft: 'auto',
		alignItems: 'flex-start',
	},
});

const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',

	'#more-fields': {
		display: 'flex',
		flexDirection: 'column',
		gap: '14px',
		background: '#f2f3f6',
		marginLeft: '250px',
		padding: '16px',
	},
	'#fields': {
		display: 'flex',
		gap: '28px',
	},
});

const FieldConatiner = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	'#key': {
		fontSize: '14px',
		fontWeight: 400,
		color: '#979797',
	},
	'#value-container': {
		display: 'flex',
		alignItems: 'center',
		paddingLeft: '2px',
		minWidth: 'auto',
		maxWidth: '150px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		fontSize: '14px',
		fontWeight: 600,
		color: '#7D7D7D',
	},
});

type Supplier = {
	id: number;
	name: string;
	phone: string;
	image: string | null;
	address: string;
	slug: string;
	slugStatus: string;
};

type CategoryObject = {
	id: number;
	value: string;
};

export type Product = {
	id: number;
	name: string;
	productName: string;
	description: string;
	groupCategory: string;
	category: string;
	subCategory: string;
	machineBrand: string;
	machineModel: string;
	brand: string;
	partComposition: string;
	partDesign: string;
	partDiameter: string;
	partDimension: string;
	partOwnership: string;
	partPerformance: string;
	partQuality: string;
	partWeight: string;
	images: string[];
	sku: string;
	model: string;
	price: string;
	quantity: number;
	supplier: Supplier;
	categoryId: number;
	categoryObject: CategoryObject;
	subCategoryId: number;
	machineBrandId: number;
	machineModelId: number;
	groupCategoryId: number;
	brandId: number;
	partCompositionId: number;
	partDesignId: number;
	partDiameterId: number;
	partDimensionId: number;
	partOwnershipId: number;
	partQualityId: number;
	partWeightId: number;
	partPerformanceId: number;
	isSelection?: boolean;
	recommend: string;
	disableRow?: boolean;
	partNumber?: string;
};

interface FieldProp {
	fieldKey: string;
	value: string;
	valueStyle?: any;
	keyStyle?: any;
}

const Field: React.FC<FieldProp> = ({ fieldKey, value, valueStyle = {}, keyStyle = {} }) => {
	if (value == 'N/A') return null;
	return (
		<FieldConatiner>
			{!!fieldKey ? (
				<div id="key" style={keyStyle}>
					{fieldKey}
				</div>
			) : null}
			<CustomTooltip title={value} placement="top">
				<div id="value-container" style={valueStyle}>
					{typeof value === 'string' ? value : ''}
				</div>
			</CustomTooltip>
		</FieldConatiner>
	);
};

const ShowDetailsButton = styled.div({
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	gap: '5px',
	fontWeight: 'bold',
	fontSize: '13px',
});

interface Props {
	data: Product;
	checkBoxSelection?: any;
	noCheckbox?: boolean;
	decreaseQuantity?: any;
	increaseQuantity?: any;
	recommendedQuantity?: any;
	showDetails?: any;
	readOnly?: boolean;
	showQuantity?: boolean;
	view?: 'default' | 'order';
	onCounterChange?: any;
	handleRowClick?: any;
	handleEditClick?: any;
	handleUpdate?: any;
	handleEditClose?: any;
	StyledTextFieldCustomerPrice?: any;
	handleCustomerPrice?: any;
	showStaticQuantity?: boolean;
}

function EditOptions({
	data,
	handleEditClick,
	handleUpdate,
	handleEditClose,
	StyledTextFieldCustomerPrice,
	handleCustomerPrice,
	isMobile,
}: any) {
	if (data?.edit) {
		return (
			<Stack direction={isMobile ? 'column' : 'row'}>
				<StyledTextFieldCustomerPrice
					id="editPrice"
					variant="standard"
					value={data?.customerPrice}
					autoFocus={data?.focus}
					onChange={(e) => {
						handleCustomerPrice(e, data);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleUpdate(data);
						}
					}}
					InputProps={{
						disableUnderline: true,
						classes: {
							root: 'MuiInputBase-root',
						},
					}}
				/>
				<div
					id="updateBtns"
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '5px',
						width: '100%',
						height: '100%',
					}}
				>
					<IconButton id="updateProductBtn" onClick={() => handleUpdate(data)}>
						<TickIcon id="updateProduct" />
					</IconButton>
					<IconButton id="cancelProductBtn" onClick={() => handleEditClose(data)}>
						<CrossIcon id="cancelProduct" />
					</IconButton>
				</div>
			</Stack>
		);
	}

	if (data?.editBtnDisable && data?.customerPrice && handleEditClick) {
		return (
			<Stack direction="row" alignItems="flex-end" spacing={1}>
				<Stack>
					<Typography variant="body1" fontSize={12}>
						Customer Price
					</Typography>
					<Typography variant="body1" fontSize={18} fontWeight={800}>
						Rs {data?.customerPrice}
					</Typography>
				</Stack>

				<CustomTooltip title="Please Select Item First" placement="top">
					<span>
						<IconButton>
							<EditIcon id="editProduct1" />
						</IconButton>
					</span>
				</CustomTooltip>
			</Stack>
		);
	}
	if (data?.customerPrice && handleEditClick)
		return (
			<Stack direction="row" alignItems="flex-end" spacing={1}>
				<Stack>
					<Typography variant="body1" fontSize={12}>
						Customer Price
					</Typography>
					<Typography variant="body1" fontSize={18} fontWeight={800}>
						Rs {data?.customerPrice}
					</Typography>
				</Stack>
				<IconButton
					disabled={data?.editBtnDisable}
					onClick={() => handleEditClick(data)}
					id="editProductBtn"
				>
					<EditIcon id="editProduct" />
				</IconButton>
			</Stack>
		);
	return null;
}

function RenderMobileCard({
	data,
	noCheckbox,
	handleCheckboxChange,
	image,
	increaseQuantity,
	decreaseQuantity,
	recommendedQuantity,
	showDetails,
	readOnly,
	onCounterChange,
	view,
	handleEditClick,
	handleUpdate,
	handleEditClose,
	StyledTextFieldCustomerPrice,
	handleCustomerPrice,
	showQuantity,
	showEditOptions,
	showDisabledCounter,
	isRestaurant,
	showStaticQuantity,
}: any) {
	return (
		<Container>
			<MobileContainer quantity={data?.quantity || 0}>
				<Stack direction="row" gap={2}>
					{!noCheckbox ? (
						<CustomCheckbox
							isChecked={data?.isSelection ? data?.isSelection : false}
							handleChange={handleCheckboxChange}
						/>
					) : null}
					<img src={image || '/placeholderImage.svg'} style={{ width: '50px', height: '50px' }} />
					<Field
						fieldKey=""
						value={data?.name || data?.productName || 'N/A'}
						valueStyle={{
							maxWidth: '340px',
							minWidth: 'auto',
							fontWeight: 700,
							color: '#1A1A1A',
							textWrap: 'auto',
						}}
					/>
				</Stack>
				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<div id="fieldsContainer">
						<div id="fields">
							{/* <Field
								fieldKey="SKU:"
								value={data?.sku || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/> */}

							<Field
								fieldKey="Brand:"
								value={data?.brand || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Machine Model:"
								value={data?.machineModel || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Composition:"
								value={data?.partComposition || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Design:"
								value={data?.partDesign || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
							<Field
								fieldKey="Dimension:"
								value={data?.partDimension || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>
						<div id="fields">
							<Field
								fieldKey="Part Number:"
								value={data?.partNumber || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Weight:"
								value={data?.partWeight || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Diameter:"
								value={data?.partDiameter || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
							{showStaticQuantity && (
								<Field
									fieldKey="Qty:"
									value={String(data?.quantity) || 'N/A'}
									valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
								/>
							)}
						</div>
					</div>
				</div>
				<Stack gap={1} mt={1}>
					<Stack direction="row" alignItems="center" justifyContent={'space-between'} spacing={1}>
						<Stack>
							<Typography variant="body1" fontSize={12}>
								{isRestaurant ? 'Price' : 'MRP'}
							</Typography>
							<Field
								fieldKey=""
								value={
									isRestaurant ? `Rs. ${data?.customerPrice || data?.price}` : `Rs. ${data?.price}`
								}
								valueStyle={{
									maxWidth: '200px',
									minWidth: 'auto',
									color: '#000000',
									fontSize: '18px',
									fontWeight: 800,
								}}
							/>
						</Stack>

						{showEditOptions && (
							<EditOptions
								data={data}
								{...{
									handleEditClick,
									handleUpdate,
									handleEditClose,
									StyledTextFieldCustomerPrice,
									handleCustomerPrice,
									isMobile: true,
								}}
							/>
						)}
						{showDisabledCounter && (
							<CustomCounter
								counter={data?.quantity}
								onIncrement={() => {}}
								onDecrement={() => {}}
								variant="round"
								allowZeroQuantity
								readOnly
							/>
						)}
						{!showEditOptions && (
							<CustomCounter
								id={data?.id}
								counter={data?.quantity}
								onIncrement={() => increaseQuantity(data?.id)}
								onDecrement={() => decreaseQuantity(data?.id)}
								variant="round"
								allowZeroQuantity
								readOnly={readOnly}
								onCounterChange={onCounterChange}
							/>
						)}
					</Stack>
					<Stack>
						{!readOnly && data?.recommend ? (
							<div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
								<span>Recommended</span>
								<span
									style={{ textDecoration: 'underline', cursor: 'pointer' }}
									onClick={
										Number(data?.quantity) !== Number(data?.recommend)
											? () => recommendedQuantity(data?.id)
											: () => {}
									}
								>
									{data?.recommend}
								</span>
							</div>
						) : null}
					</Stack>

					<CustomButton
						color="primary"
						title="Show Details"
						disabled={false}
						handleClick={() => showDetails(data)}
						styles={{
							width: '100%',
							height: '30px !important',
							borderRadius: '4px',
							fontSize: '14px',
						}}
					/>
				</Stack>
			</MobileContainer>
		</Container>
	);
}

function RenderProductCardForViewTypeOrder({
	data,
	handleCheckboxChange,
	noCheckbox,
	increaseQuantity,
	decreaseQuantity,
	recommendedQuantity,
	showDetails,
	readOnly,
	onCounterChange,
	view,
	showQuantity,
	isRestaurant,
}: any) {
	const isMobileWidth = IsMobileWidth();
	let image = data?.images?.[0]?.url || data?.images?.[0]?.image;
	if (!image) {
		image = data.image;
	}

	if (isMobileWidth) {
		return (
			<RenderMobileCard
				{...{
					data,
					noCheckbox,
					handleCheckboxChange,
					image,
					increaseQuantity,
					decreaseQuantity,
					recommendedQuantity,
					showDetails,
					readOnly,
					onCounterChange,
					view,
					showQuantity,
					isRestaurant,
				}}
			/>
		);
	}
	return (
		<Container>
			<StyledContainer style={Number(data?.quantity) > 0 ? { background: '#FFFDF5' } : {}}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						{!noCheckbox ? (
							<CustomCheckbox
								isChecked={data?.isSelection ? data?.isSelection : false}
								handleChange={handleCheckboxChange}
							/>
						) : null}
						<div id="image-containner">
							<img src={image || '/placeholderImage.svg'} />
						</div>
					</div>

					<div id="fieldsContainer">
						<div id="fields">
							<Field
								fieldKey=""
								value={data?.name || data?.productName || 'N/A'}
								valueStyle={{
									maxWidth: '340px',
									minWidth: 'auto',
									fontWeight: 700,
									color: '#1A1A1A',
								}}
							/>
						</div>

						<div id="fields">
							{/* <Field
								fieldKey="SKU:"
								value={data?.sku || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/> */}

							<Field
								fieldKey="Brand:"
								value={data?.brand || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Machine Model:"
								value={data?.machineModel || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Composition:"
								value={data?.partComposition || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Design:"
								value={data?.partDesign || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Dimension:"
								value={data?.partDimension || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Weight:"
								value={data?.partWeight || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Diameter:"
								value={data?.partDiameter || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>
						<div id="fields">
							<Field
								fieldKey="Part Number:"
								value={data?.partNumber || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>
					</div>
				</div>

				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Divider
						orientation="vertical"
						sx={{ background: '#EDEDED', height: '140px', width: '1px', marginRight: '6px' }}
					/>
					<div id="quantity-recommend-container">
						<Field
							fieldKey=""
							value={
								isRestaurant ? `Rs. ${data?.customerPrice || data?.price}` : `Rs. ${data?.price}`
							}
							valueStyle={{
								maxWidth: '200px',
								minWidth: 'auto',
								color: '#000000',
								fontSize: '18px',
								fontWeight: 800,
							}}
						/>
						<CustomCounter
							id={data?.id}
							counter={data?.quantity}
							onIncrement={() => increaseQuantity(data?.id)}
							onDecrement={() => decreaseQuantity(data?.id)}
							variant="round"
							allowZeroQuantity
							readOnly={readOnly}
							onCounterChange={onCounterChange}
						/>
						{!readOnly ? (
							<div style={{ display: 'flex', gap: '10px' }}>
								<span>Recommended</span>
								<span
									style={{ textDecoration: 'underline', cursor: 'pointer' }}
									onClick={
										Number(data?.quantity) !== Number(data?.recommend)
											? () => recommendedQuantity(data?.id)
											: () => {}
									}
								>
									{data?.recommend}
								</span>
							</div>
						) : null}
						<CustomButton
							color="primary"
							title="Show Details"
							disabled={false}
							handleClick={() => showDetails(data)}
							styles={{
								width: '180px',
								height: '30px !important',
								borderRadius: '4px',
								fontSize: '14px',
							}}
						/>
					</div>
				</div>
			</StyledContainer>
		</Container>
	);
}

const ProductCard: React.FC<Props> = ({
	data,
	checkBoxSelection,
	noCheckbox = false,
	increaseQuantity,
	decreaseQuantity,
	recommendedQuantity,
	showDetails,
	readOnly = false,
	showQuantity = true,
	view = 'default',
	onCounterChange,
	handleRowClick,
	handleEditClick,
	handleUpdate,
	handleEditClose,
	StyledTextFieldCustomerPrice,
	handleCustomerPrice,
	showStaticQuantity = false,
}) => {
	const isMobileWidth = IsMobileWidth();
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;
	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (checkBoxSelection) {
			checkBoxSelection(data?.id, event.target.checked);
		}
	};

	if (view === 'order') {
		return (
			<RenderProductCardForViewTypeOrder
				{...{
					data,
					handleCheckboxChange,
					noCheckbox,
					increaseQuantity,
					decreaseQuantity,
					recommendedQuantity,
					showDetails,
					readOnly,
					onCounterChange,
					view,
					handleEditClick,
					handleUpdate,
					handleEditClose,
					StyledTextFieldCustomerPrice,
					handleCustomerPrice,
					showQuantity: true,
					isRestaurant,
					showStaticQuantity,
				}}
			/>
		);
	}

	const showDisabledCounter = showQuantity && Number(data?.quantity) > 0;
	const image = data?.images?.[0]?.url || data?.images?.[0]?.image || '/placeholderImage.svg';
	if (isMobileWidth) {
		return (
			<RenderMobileCard
				{...{
					data,
					noCheckbox,
					handleCheckboxChange,
					image,
					increaseQuantity,
					decreaseQuantity,
					recommendedQuantity,
					showDetails,
					readOnly,
					onCounterChange,
					showQuantity,
					showEditOptions: true,
					showDisabledCounter,
					isRestaurant,
					handleEditClick,
					handleUpdate,
					handleEditClose,
					StyledTextFieldCustomerPrice,
					handleCustomerPrice,
					showStaticQuantity,
				}}
			/>
		);
	}

	return (
		<Container style={data?.disableRow ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
			<StyledContainer>
				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						{!noCheckbox ? (
							<CustomCheckbox
								isChecked={data?.isSelection ? data?.isSelection : false}
								handleChange={handleCheckboxChange}
							/>
						) : null}
						<div id="image-containner">
							<img src={image} />
						</div>
					</div>

					<div id="fieldsContainer">
						<div id="fields">
							<Field
								fieldKey=""
								value={data.name || 'N/A'}
								valueStyle={{
									maxWidth: '340px',
									minWidth: 'auto',
									fontWeight: 700,
									color: '#1A1A1A',
								}}
							/>
						</div>

						<div id="fields">
							{/* <Field
								fieldKey="SKU:"
								value={data.sku || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/> */}

							<Field
								fieldKey="Brand:"
								value={data?.brand || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Machine Model:"
								value={data?.machineModel || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Composition:"
								value={data?.partComposition || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Design:"
								value={data?.partDesign || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>

						<div id="fields">
							<Field
								fieldKey="Dimension:"
								value={data?.partDimension || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Weight:"
								value={data?.partWeight || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>

							<Field
								fieldKey="Diameter:"
								value={data?.partDiameter || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>
						<div id="fields">
							<Field
								fieldKey="Part Number:"
								value={data?.partNumber || 'N/A'}
								valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
							/>
						</div>
					</div>
				</div>

				<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
					<Divider
						orientation="vertical"
						sx={{ background: '#EDEDED', height: '140px', width: '1px', marginRight: '6px' }}
					/>
					<div id="quantity-recommend-container">
						<Stack spacing={1}>
							<Typography variant="body1" fontSize={12}>
								{isRestaurant ? 'Price' : 'MRP'}
							</Typography>
							<Field
								fieldKey=""
								value={
									isRestaurant ? `Rs. ${data?.customerPrice || data?.price}` : `Rs. ${data?.price}`
								}
								valueStyle={{
									maxWidth: '200px',
									minWidth: 'auto',
									color: '#000000',
									fontSize: '18px',
									fontWeight: 800,
								}}
							/>
							<EditOptions
								data={data}
								{...{
									handleEditClick,
									handleUpdate,
									handleEditClose,
									StyledTextFieldCustomerPrice,
									handleCustomerPrice,
								}}
							/>
						</Stack>

						{showStaticQuantity && (
							<Stack spacing={1}>
								<Typography variant="body1" fontSize={12}>
									Qty: {data?.quantity}
								</Typography>
							</Stack>
						)}

						{showDisabledCounter ? (
							<CustomCounter
								counter={data?.quantity}
								onIncrement={() => {}}
								onDecrement={() => {}}
								variant="round"
								allowZeroQuantity
								readOnly
							/>
						) : null}
						<CustomButton
							color="primary"
							title="Show Details"
							disabled={false}
							handleClick={() => showDetails(data)}
							styles={{
								width: '180px',
								height: '30px !important',
								borderRadius: '4px',
								fontSize: '14px',
							}}
						/>
						{handleRowClick && (
							<CustomButton
								color="secondary"
								title="Edit"
								disabled={false}
								handleClick={() => handleRowClick(data)}
								styles={{
									width: '180px',
									height: '30px !important',
									borderRadius: '4px',
									fontSize: '14px',
								}}
							/>
						)}
					</div>
				</div>
			</StyledContainer>
		</Container>
	);
};

export default ProductCard;
